import { useState } from 'react';
import { AuditOutlined, FileTextOutlined, ReloadOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { cn } from '../../../lib/cn';
import { ChiTietTaiLieu } from '../components/ChiTietTaiLieu';
import { QUYEN, THU_TU_TRANG_THAI_TAI_LIEU, TRANG_THAI_TAI_LIEU } from '../constants';
import { useDanhSachTaiLieu } from '../hooks/useTriThuc';
import type { TrangThaiTaiLieu } from '../types';

/**
 * Kiểm duyệt & phê duyệt tri thức (`/tri-thuc/kiem-duyet`, phân hệ `KNOWLEDGE_REVIEW`): danh sách tài liệu
 * bên trái, chi tiết và thao tác của tài liệu đang chọn bên phải. Tài liệu được duyệt thành một tệp trong
 * bộ tri thức và vào hàng đợi index. Màn hẹp thì hai khung xếp chồng.
 */
export function KiemDuyetView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const duocTaiLen = useModulePermission(QUYEN.taiLieuTao);

  const [nhap, setNhap] = useState('');
  const [timKiem, setTimKiem] = useState('');
  const [trangThai, setTrangThai] = useState<TrangThaiTaiLieu | undefined>();
  const [chon, setChon] = useState<string | undefined>();

  const q = useDanhSachTaiLieu(trangThai, undefined, timKiem);
  const tieuDe = t('triThuc.duyet.tieuDe');
  const coLoc = Boolean(timKiem || trangThai);
  const loiTaiDau = q.error && q.dong.length === 0 && !q.isFetchNextPageError;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.triThucAi') }, { label: t('nav.muc.kiemDuyetPheDuyet') }]}
          titleIcon={<AuditOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('triThuc.duyet.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              {duocTaiLen && (
                <Button variant="primary" size="md" icon={<UploadOutlined />} onClick={() => dieuHuong('/tri-thuc/upload')}>
                  {t('triThuc.duyet.taiTaiLieu')}
                </Button>
              )}
            </>
          }
        />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <section
            aria-label={t('triThuc.duyet.danhSach')}
            className="flex shrink-0 flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm lg:w-80"
          >
            <div className="space-y-2 border-b border-default p-3">
              <Input
                allowClear
                prefix={<SearchOutlined className="text-content-muted" />}
                placeholder={t('triThuc.duyet.timKiem')}
                aria-label={t('triThuc.duyet.timKiem')}
                value={nhap}
                onChange={(e) => {
                  setNhap(e.target.value);
                  if (!e.target.value) setTimKiem('');
                }}
                onPressEnter={() => setTimKiem(nhap.trim())}
                onBlur={() => setTimKiem(nhap.trim())}
              />
              <Select<TrangThaiTaiLieu>
                allowClear
                className="w-full"
                placeholder={t('triThuc.duyet.moiTrangThai')}
                aria-label={t('chung.trangThai')}
                options={THU_TU_TRANG_THAI_TAI_LIEU.map((s) => ({ value: s, label: t(TRANG_THAI_TAI_LIEU[s].khoa) }))}
                value={trangThai}
                onChange={setTrangThai}
              />
            </div>

            {loiTaiDau ? (
              <div role="alert" className="space-y-2 p-3">
                <p className="text-sm text-danger-700">{t('triThuc.duyet.loiDanhSach', { loi: dichLoi(q.error) })}</p>
                <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              </div>
            ) : q.isLoading ? (
              <div className="space-y-2 p-3" aria-busy="true">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-16 rounded" />
                ))}
              </div>
            ) : q.dong.length === 0 ? (
              <Empty
                icon={FileTextOutlined}
                // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                title={t(coLoc ? 'triThuc.duyet.rongLoc' : 'triThuc.duyet.rongThat')}
                description={coLoc ? undefined : t('triThuc.duyet.rongThatMo')}
                action={
                  coLoc || !duocTaiLen ? undefined : (
                    <Button variant="primary" size="md" icon={<UploadOutlined />} onClick={() => dieuHuong('/tri-thuc/upload')}>
                      {t('triThuc.duyet.taiTaiLieu')}
                    </Button>
                  )
                }
              />
            ) : (
              <>
                <ul className="max-h-[36rem] space-y-1 overflow-y-auto p-2">
                  {q.dong.map((d) => {
                    const dangChon = d.id === chon;
                    const tt = TRANG_THAI_TAI_LIEU[d.status];
                    return (
                      <li key={d.id}>
                        <button
                          type="button"
                          aria-current={dangChon ? 'true' : undefined}
                          onClick={() => setChon(d.id)}
                          className={cn(
                            'w-full rounded border px-3 py-2.5 text-left transition-colors',
                            dangChon ? 'border-primary-200 bg-primary-50' : 'border-transparent hover:bg-subtle',
                          )}
                        >
                          <span className="flex items-start justify-between gap-2">
                            <span className="line-clamp-2 min-w-0 text-sm font-medium text-content-primary">{d.title}</span>
                            <Badge variant={tt.variant}>{t(tt.khoa)}</Badge>
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-x-1.5 text-2xs text-content-muted">
                            <span className="font-mono">{d.current_version}</span>
                            <span aria-hidden>·</span>
                            <span className="max-w-32 truncate">{d.knowledge_base_name}</span>
                            <span aria-hidden>·</span>
                            <span>{ngayVN(d.updated_at)}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-default px-3 py-2">
                  <span className="text-xs text-content-muted">
                    {t(q.hasNextPage ? 'triThuc.duyet.dangHienCoThem' : 'triThuc.duyet.dangHien', { so: soVN(q.dong.length, locale) })}
                  </span>
                  {q.isFetchNextPageError && (
                    <span role="alert" className="w-full text-xs text-danger-700">
                      {t('triThuc.loiTaiThem', { loi: dichLoi(q.error) })}
                    </span>
                  )}
                  {q.hasNextPage && (
                    <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                      {q.isFetchNextPageError ? t('chung.thuLai') : t('chung.taiThem')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </section>

          <section
            aria-label={t('triThuc.duyet.chiTiet')}
            className="min-w-0 flex-1 rounded-lg border border-default bg-surface shadow-sm"
          >
            {chon ? (
              <ChiTietTaiLieu id={chon} />
            ) : (
              <Empty icon={FileTextOutlined} title={t('triThuc.duyet.chuaChon')} description={t('triThuc.duyet.chuaChonMo')} />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
